import { random } from "lodash";

import { EError, ILogData } from "../../web-shared-components/helpers/logger/ILogger";
import { theConfig, theLogger } from "../globals";
import { generateGUID } from "./common";
import { EAsnLogonErrorEnumEX } from "./ErrorInfoHelper";

// Result of the get UC Proxy request
interface IGetUCWS {
	redirect: string;
}

/**
 * UCCHelper class
 */
export default class UCCHelper {
	/**
	 * The Loggers getLogData callback (used in all the log methods called in this class, add the classname to every log entry)
	 * @returns - an ILogData log data object provided additional data for all the logger calls in this class
	 */
	public static getLogData(): ILogData {
		return {
			className: "UCCHelper"
		};
	}

	/**
	 * Get login challenges from ucserver
	 * @param ucServerURI - ucserver uri
	 * @param ucsID - ucsid
	 * @returns - error of type EAsnLogonErrorEnumEX or undefined
	 */
	public static async getLoginChallenges(ucServerURI: string, ucsID?: string): Promise<number | string[]> {
		const url = ucServerURI + "/ws/client/createsession";
		const headers: Record<string, string> = {
			"x-epid": generateGUID(),
			"access-control-request-headers": "WWW-Authenticate",
			"Content-Type": "application/json"
		};

		if (ucsID) headers["x-ucsid"] = ucsID;

		const options = {
			method: "POST",
			headers
		};

		let sessionResponse: Response | undefined;

		try {
			sessionResponse = await fetch(url, options);
			const auth = sessionResponse.headers.get("WWW-Authenticate");

			if (!auth) return EAsnLogonErrorEnumEX.exFetchError;

			const splitByBasic = auth.split(/(?=Basic)/g);

			const matchScopesRegex = /(?<=scope=")(.*?)(?=")/gm;
			const allAuthChallenges: string[] = [];
			for (const basic of splitByBasic) {
				if (basic.search("Basic realm") === -1) continue;
				const scopesMatches = [...basic.matchAll(matchScopesRegex)];
				if (scopesMatches.length === 0) allAuthChallenges.push("basic");
				const scopes = Array.from(scopesMatches.map((item) => item[0]));
				allAuthChallenges.push(...scopes);
			}

			theLogger.info("Login challenges found", "getLoginChallenges", this.getLogData(), {
				challenges: allAuthChallenges
			});
			return allAuthChallenges;
		} catch (error) {
			theLogger.error("Error fetching login challenges", "getLoginChallenges", this, { error, sessionResponse });
			return EAsnLogonErrorEnumEX.exFetchError;
		}
	}

	/**
	 * Gets the URI of UCServer from UCController.
	 * @param ucsid - the ucsid
	 * @param uccontrollerUrl - the uccontroller endpoint
	 * @returns - a promise with a string containing the URI, or an error of type EAsnLogonErrorEnumEX
	 */
	public static async getUriFromUCC(ucsid: string, uccontrollerUrl?: URL): Promise<string | number> {
		if (!uccontrollerUrl) {
			theLogger.error(
				"You need to setup the uccontroller URL",
				"getUriFromUCC",
				this.getLogData(),
				{},
				EError.InvalidUCControllerURL
			);
			return EAsnLogonErrorEnumEX.exInvalidUCControllerURL;
		}
		const url = new URL(uccontrollerUrl);
		url.pathname += "controller/client/ucws";
		url.search = "?ucsid=" + ucsid + "&needswcs=1";

		let getUcwebUrlResponse: Response | undefined;

		try {
			getUcwebUrlResponse = await fetch(url, { cache: "no-store" });
			const ucwebUrlData = (await getUcwebUrlResponse.json()) as IGetUCWS;
			if (ucwebUrlData && ucwebUrlData.redirect) return ucwebUrlData.redirect;
			else {
				theLogger.error("Invalid ucsid", "getUriFromUCC", this.getLogData(), { ucwebUrlData }, EError.InvalidUCSID);
				return EAsnLogonErrorEnumEX.exInvalidUcsidFromUCController;
			}
		} catch (error) {
			theLogger.error(
				"Fetch error",
				"getUriFromUCC",
				this.getLogData(),
				{ error, response: getUcwebUrlResponse },
				EError.FetchFailed
			);
			return EAsnLogonErrorEnumEX.exFetchError;
		}
	}

	/**
	 * get reconnect backoff time for waiting in  the reconnecting related parameters
	 * @param retryCount - counter value of current retry
	 * @returns - time in milliseconds the caller should wait before initiating another reconnect
	 */
	public static async getReconnectBackoffTimeMS(retryCount: number): Promise<number> {
		const { reconnectingInterval, maxReconnectWaitTimeSeconds } = theConfig.config;

		// Berechnung des Zufallsfaktors (between 1ms and +200ms)
		const randomFactor = random(1, reconnectingInterval / 10);

		// Berechnung der Retry-Zeit in Millisekunden
		let retryTimeMS = 100 * (retryCount + 1) + randomFactor;

		// dont increase above maximum waittime
		if (retryTimeMS > maxReconnectWaitTimeSeconds * 1000)
			retryTimeMS = maxReconnectWaitTimeSeconds * 1000 + randomFactor;

		theLogger.info("Reconnect BackoffTime set", "getReconnectBackoffTimeMS", this.getLogData(), {
			retryTimeMS,
			retryCount,
			reconnectingInterval,
			randomFactor
		});
		return retryTimeMS;
	}
}
